import React from 'react';

import { UiButton, useFormat } from '@nfctron/nexus-react';

import { Indicator } from '@mantine/core';
import { ChevronRightIcon, ShoppingCartIcon } from 'lucide-react';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { useCartStore } from '../../stores/cartStore';

/**
 * Event/Cart/ProductsViewFooter atom component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ProductsViewFooterAtom: AtomComponent = () => {
	const format = useFormat();
	const cartConfig = useCartStore((s) => s.cartConfig);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ProductsViewFooterAtom.displayName);
	const nextVisible = useCartStore((s) => s.getNextCartView({ prop: 'isVisible' }));
	const nextVisibleState = useCartStore((s) => s.getCartViewEvaluated({ view: nextVisible }));
	const setCartView = useCartStore((s) => s.setCartView);
	const cartedTickets = useCartStore((s) => s.cartedTickets);
	const cartedTicketsWithExtraProducts = useCartStore((s) => s.cartedTickets.filter((ticket) => ticket.extraProducts.length > 0));
	const cartTotal = useCartStore((s) => s.getCartTotal());

	return (
		<div className="mt-4.5 flex h-full w-full flex-col justify-around gap-3 lg:mt-7 lg:flex-row lg:items-center lg:justify-between xl:mt-0">
			<div className="flex items-center justify-between gap-2 lg:flex-col lg:items-start">
				{/* label */}
				<div className="flex items-center gap-4">
					{/* carted ticket count */}
					<Indicator label={<span className="text-xs font-bold">{format.number(cartedTickets.length)}</span>} size="16px" color="lucky-point">
						<ShoppingCartIcon className="size-5" />
					</Indicator>
					{/* total sum heading */}
					<span className="text-lg font-medium text-zinc-900">{t('totalSum.text')}</span>
				</div>

				{/* price */}
				<div>
					{/* total sum for the whole order */}
					<span className="text-lucky-point-500 ml-auto flex items-end gap-2 text-2xl font-bold md:text-4xl">
						{/* original price (if different) */}
						{cartTotal.total !== cartTotal.originalTotal && (
							<span className="order-0 text-sm font-normal text-zinc-500 line-through lg:order-1 lg:text-base">
								{format.price(cartTotal.originalTotal, cartConfig.currencyId)}
							</span>
						)}
						{/* price */}
						{format.price(cartTotal.total, cartConfig.currencyId)}
					</span>
					{/* sum vat disclaimer */}
					<span className="hidden text-sm text-zinc-500 lg:block">{t('totalSum.disclaimerText')}</span>
				</div>
			</div>

			{/* continue button */}
			<div className="text-right">
				<UiButton
					className="text-md lg:w-unset h-12 w-full min-w-[200px]"
					variant="subtle"
					color="lucky-point"
					rightSection={<ChevronRightIcon />}
					disabled={!nextVisibleState.isEnabled}
					onClick={async () => {
						if (nextVisibleState.isEnabled) {
							setCartView(nextVisible);
						}
					}}
				>
					{cartedTicketsWithExtraProducts.length > 0 ? t('continueButton.text') : t('skipButton.text')}
				</UiButton>
			</div>
		</div>
	);
};
ProductsViewFooterAtom.displayName = 'Event/Cart/ProductsViewFooterAtom';
